import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import lilyFounder from "../../static/media/AboutUs/lilys.webp";
import logo from "../../static/media/logo.webp";

const PRIMARY = "#06BBC7";
const YELLOW = "#F7C948";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const FounderPresentation = () => {
  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        maxWidth: "100vw",
        bgcolor: "#ffffff",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 6, sm: 8, md: 10 },
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1180,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
          gap: { xs: 4.5, md: 7 },
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        <MotionBox
          initial={{ opacity: 0, x: -35 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.25 }}
          sx={{
            width: "100%",
            maxWidth: { xs: 330, sm: 410, md: 465 },
            mx: { xs: "auto", md: 0 },
            position: "relative",
            boxSizing: "border-box",
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ y: -5, scale: 1.03 }}
            transition={{ duration: 0.45, ease: "easeOut", delay: 0.1 }}
            viewport={{ once: true, amount: 0.3 }}
            sx={{
              width: { md: 148 },
              height: { md: 148 },
              bgcolor: "#ffffff",
              border: `4px solid ${PRIMARY}`,
              borderRadius: "50%",
              display: { xs: "none", md: "grid" },
              placeItems: "center",
              position: "absolute",
              zIndex: 2,
              top: -38,
              right: -24,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo Lily's Sunflower"
              sx={{
                width: "82%",
                height: "82%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </MotionBox>

          <MotionBox
            whileHover={{ y: -6, scale: 1.012 }}
            transition={{ duration: 0.24, ease: "easeOut" }}
            sx={{
              width: "100%",
              bgcolor: PRIMARY,
              borderRadius: { xs: "24px", sm: "30px" },
              p: { xs: 1, sm: 1.4 },
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={lilyFounder}
              alt="Liliana Oña, fundadora de Lily's Sunflower"
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: { xs: 365, sm: 470, md: 535 },
                objectFit: "cover",
                objectPosition: "center top",
                display: "block",
                borderRadius: { xs: "19px", sm: "24px" },
              }}
            />
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: true, amount: 0.35 }}
            sx={{
              mt: 2,
              bgcolor: "#f5fdfe",
              borderLeft: `4px solid ${PRIMARY}`,
              borderRadius: "18px",
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.8, sm: 2 },
              textAlign: "left",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                color: "#111827",
                fontSize: { xs: "0.95rem", sm: "1rem" },
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              “Me descubrí, esto es lo mío”.
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                color: "#6b7280",
                fontSize: "0.88rem",
                lineHeight: 1.5,
                fontWeight: 400,
              }}
            >
              La frase que marcó el inicio de Lily´s Sunflower.
            </Typography>
          </MotionBox>
        </MotionBox>

        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            textAlign: { xs: "center", md: "left" },
            boxSizing: "border-box",
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 18, scale: 0.94 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ y: -4, scale: 1.03 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
            sx={{
              mb: 3,
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: { xs: 118, sm: 138 },
                height: { xs: 118, sm: 138 },
                borderRadius: "50%",
                border: `4px solid ${PRIMARY}`,
                bgcolor: "#ffffff",
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo Lily's Sunflower"
                sx={{
                  width: "82%",
                  height: "82%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </MotionBox>

          <Typography
            component="p"
            sx={{
              m: 0,
              mb: 1.4,
              color: PRIMARY,
              fontSize: { xs: "0.86rem", sm: "0.95rem" },
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
            }}
          >
            Nuestra fundadora
          </Typography>

          <Typography
            component="h2"
            sx={{
              m: 0,
              mb: 1.7,
              color: "#111827",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.04,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              overflowWrap: "break-word",
            }}
          >
            Liliana Oña
          </Typography>

          <Typography
            component="h3"
            sx={{
              m: 0,
              mb: { xs: 2.4, sm: 3 },
              color: "#1f2937",
              fontSize: { xs: "1.35rem", sm: "1.85rem", md: "2.25rem" },
              lineHeight: 1.18,
              fontWeight: 700,
              letterSpacing: "-0.03em",
              overflowWrap: "break-word",
            }}
          >
            La vocación que dio vida a Lily´s Sunflower
          </Typography>

          <Stack
            spacing={{ xs: 1.8, sm: 2.1 }}
            sx={{
              width: "100%",
              maxWidth: { xs: 520, md: "none" },
              mx: { xs: "auto", md: 0 },
              color: "#4b5563",
              fontSize: { xs: "0.95rem", sm: "1.04rem" },
              lineHeight: { xs: 1.68, sm: 1.82 },
              fontWeight: 400,
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                m: 0,
                fontSize: "inherit",
                lineHeight: "inherit",
                fontWeight: 400,
              }}
            >
              Lily´s Sunflower nace en 2017, cuando Liliana inició su formación
              en Cosmetología y descubrió que el cuidado estético era su
              verdadera vocación. Desde sus primeros días de clases sintió que
              había encontrado un camino para servir, cuidar y transformar con
              propósito.
            </Typography>

            <Typography
              sx={{
                m: 0,
                fontSize: "inherit",
                lineHeight: "inherit",
                fontWeight: 400,
              }}
            >
              Su paso por dos escuelas en distintas ciudades fortaleció sus
              conocimientos en diagnóstico, cuidado de la piel, tratamientos
              faciales, corporales, cuero cabelludo y masajes terapéuticos.
            </Typography>

            <Typography
              sx={{
                m: 0,
                fontSize: "inherit",
                lineHeight: "inherit",
                fontWeight: 400,
              }}
            >
              Hoy, desde Ballenita, Lily´s Sunflower brinda una atención
              estética personalizada, consciente y eficaz, manteniendo el
              compromiso de actualizarse para ofrecer tratamientos seguros y a la
              vanguardia.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              width: "100%",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: { xs: "center", md: "flex-start" },
              maxWidth: { xs: 295, sm: "none" },
              mx: { xs: "auto", md: 0 },
              mt: { xs: 3.6, sm: 4.4 },
              boxSizing: "border-box",
            }}
          >
            <MotionButton
              component="a"
              href="/agendar-cita"
              variant="contained"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, scale: 1.035 }}
              whileTap={{ scale: 0.96 }}
              transition={{ duration: 0.24, ease: "easeOut", delay: 0.15 }}
              viewport={{ once: true, amount: 0.35 }}
              sx={{
                bgcolor: YELLOW,
                color: "#1f2937",
                px: 4,
                py: 1.18,
                width: { xs: "100%", sm: "auto" },
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#efbd35",
                  boxShadow: "0 14px 28px rgba(247, 201, 72, 0.28)",
                },
              }}
            >
              Comunícate con nosotros
            </MotionButton>
          </Stack>

          <Box
            sx={{
              mt: { xs: 4.3, sm: 5 },
              width: "100%",
              maxWidth: "100%",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: { xs: 2.3, sm: 2 },
              textAlign: { xs: "center", sm: "left" },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                borderTop: `3px solid ${PRIMARY}`,
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#111827",
                  fontSize: "1rem",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Desde 2017
              </Typography>

              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  fontWeight: 400,
                }}
              >
                Vocación, formación y experiencia estética
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `3px solid ${PRIMARY}`,
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#111827",
                  fontSize: "1rem",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Ballenita
              </Typography>

              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  fontWeight: 400,
                }}
              >
                Centro estético en Santa Elena
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `3px solid ${PRIMARY}`,
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#111827",
                  fontSize: "1rem",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Atención personalizada
              </Typography>

              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  fontWeight: 400,
                }}
              >
                Tratamientos faciales, corporales y terapéuticos
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FounderPresentation;